import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, SearchIcon, Loader2Icon, ChevronRightIcon, ChevronLeftIcon, XIcon, FileSpreadsheetIcon, ArrowRightIcon, PenLineIcon, FolderIcon, CheckCircle2Icon, ChevronDownIcon, PlusIcon, SettingsIcon, ClockIcon, SendIcon, LightbulbIcon, DatabaseIcon, FileIcon } from 'lucide-react';
const STEP_DURATION = 15000;
const steps = [{
  id: 1,
  title: 'Start with the outcome',
  description: "Describe your goal in plain language. Whether it's a prospecting list, competitive analysis, or sourcing workflow."
}, {
  id: 2,
  title: 'Refine with a few questions',
  description: 'Openclaw asks targeted questions to understand scope, filters, and priorities.'
}, {
  id: 3,
  title: 'Execute with full visibility',
  description: 'Watch each step run live with real-time progress and credit approvals.'
}];
function Step1Illustration() {
  return <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg mx-auto overflow-hidden" data-id="element-2815">
      <div className="p-6 md:p-8 bg-white" data-id="element-2816">
        {/* User message */}
        <div className="flex justify-end mb-8" data-id="element-2817">
          <div className="bg-blue-600 rounded-2xl rounded-tr-sm px-5 py-4 text-[13px] leading-relaxed text-white max-w-[85%] shadow-md shadow-blue-600/10" data-id="element-2818">
            help me prospect, source, score sales leads and write personalized
            emails for a RCM software for dental clinics in Columbus Ohio
          </div>
        </div>

        {/* AI response */}
        <div className="space-y-5 pl-1" data-id="element-2819">
          <div className="flex items-center gap-3" data-id="element-2820">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-2821">
              <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2822" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-2823">
              Thinking...
            </span>
            <Loader2Icon className="w-3.5 h-3.5 animate-spin text-blue-500" data-id="element-2824" />
          </div>

          <div className="flex items-center gap-3 pl-10" data-id="element-2825">
            <SearchIcon className="w-4 h-4 text-slate-400" data-id="element-2826" />
            <span className="text-sm text-slate-500" data-id="element-2827">Loading tools</span>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100" data-id="element-2828">
              Result
            </span>
          </div>

          <div className="pl-10 mt-1 space-y-2" data-id="element-2829">
            <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-[11px] font-mono text-slate-400 leading-relaxed" data-id="element-2830">
              <div data-id="element-2831">→ search_local_businesses(200M+ records)</div>
              <div className="text-slate-500" data-id="element-2832">
                → filter: dental clinics, Columbus OH
              </div>
              <div data-id="element-2833">→ enrich_contacts, score_leads, draft_emails</div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function Step2Illustration() {
  const [activeCard, setActiveCard] = useState(0);
  const [cardProgress, setCardProgress] = useState(0);
  useEffect(() => {
    setCardProgress(0);
    const startTime = Date.now();
    const duration = 3500;
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setCardProgress(Math.min(elapsed / duration * 100, 100));
    }, 50);
    const advanceTimer = setTimeout(() => {
      setActiveCard(prev => (prev + 1) % 4);
    }, duration);
    return () => {
      clearInterval(progressInterval);
      clearTimeout(advanceTimer);
    };
  }, [activeCard]);
  const cards = [{
    step: '1 of 4',
    question: 'What is your RCM software called and what are its top differentiators?',
    options: ['I will describe it', 'Generic RCM pitch']
  }, {
    step: '2 of 4',
    question: 'Who is the ideal decision maker you want to reach?',
    options: ['Office Manager', 'Practice Owner', 'Both']
  }, {
    step: '3 of 4',
    question: 'What email tone do you want?',
    options: ['Consultative', 'Direct and punchy', 'Warm and personal']
  }, {
    step: '4 of 4',
    question: 'How many leads do you want me to pull?',
    options: ['50+ leads', '100+ leads', 'Max available']
  }];
  return <div className="w-full max-w-md mx-auto relative h-[380px] flex items-center justify-center" data-id="element-2834">
      {/* Stacked card shadows */}
      <div className="absolute inset-x-6 top-12 bottom-4 bg-slate-100 rounded-2xl -z-20 translate-y-6 scale-[0.90]" data-id="element-2835" />
      <div className="absolute inset-x-3 top-6 bottom-2 bg-slate-50 rounded-2xl border border-slate-200/50 -z-10 translate-y-3 scale-[0.95]" data-id="element-2836" />

      <div className="bg-white rounded-2xl border border-slate-200 w-full p-6 md:p-8 shadow-xl relative z-10" data-id="element-2837">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6" data-id="element-2838">
          <p className="text-[15px] text-slate-800 font-semibold leading-relaxed pr-4" data-id="element-2839">
            {cards[activeCard].question}
          </p>
          <div className="flex items-center gap-1.5 shrink-0 pt-1" data-id="element-2840">
            <button onClick={() => setActiveCard(Math.max(0, activeCard - 1))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-30 disabled:hover:bg-transparent" disabled={activeCard === 0} data-id="element-2841">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-2842" />
            </button>
            <span className="tabular-nums font-medium text-slate-500 text-xs px-1.5" data-id="element-2843">
              {cards[activeCard].step}
            </span>
            <button onClick={() => setActiveCard(Math.min(3, activeCard + 1))} className="w-8 h-8 rounded-lg border border-blue-200 bg-blue-50 flex items-center justify-center text-blue-500 hover:bg-blue-100 hover:border-blue-300 transition-all disabled:opacity-30 disabled:bg-transparent disabled:border-slate-200 disabled:text-slate-400" disabled={activeCard === 3} data-id="element-2844">
              <ChevronRightIcon className="w-4 h-4" data-id="element-2845" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mb-5" data-id="element-2846">
          <div className="h-full bg-blue-500 rounded-full transition-none" style={{
          width: `${cardProgress}%`
        }} data-id="element-2847" />
        </div>

        {/* Options */}
        <div className="space-y-2" data-id="element-2848">
          {cards[activeCard].options.map((opt, i) => <div key={i} className={`flex items-center gap-3.5 p-3.5 rounded-xl border transition-all cursor-pointer group ${i === 0 ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`} data-id="element-2849">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-2850">
                {i + 1}
              </div>
              <span className={`text-sm font-medium ${i === 0 ? 'text-blue-900' : 'text-slate-600'}`} data-id="element-2851">
                {opt}
              </span>
              {i === 0 && <ArrowRightIcon className="w-4 h-4 ml-auto text-blue-400" data-id="element-2852" />}
            </div>)}

          {/* Something else + Skip */}
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-slate-100" data-id="element-2853">
            <div className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" data-id="element-2854">
              <PenLineIcon className="w-3.5 h-3.5" data-id="element-2855" />
              Something else
            </div>
            <button className="px-4 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-2856">
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Step3Illustration() {
  return <div className="w-full max-w-2xl mx-auto" data-id="element-2857">
      <div className="w-full rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-xl flex flex-col h-[420px] font-sans text-sm" data-id="element-2858">
        <div className="flex flex-1 overflow-hidden" data-id="element-2859">
          {/* Left: Progress sidebar */}
          <div className="hidden md:flex w-44 border-r border-slate-100 flex-col bg-slate-50/80 p-3" data-id="element-2860">
            <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-3 px-1" data-id="element-2861">
              Workflow
            </div>
            <div className="space-y-2.5 relative before:absolute before:inset-y-0 before:left-[7px] before:w-px before:bg-slate-200" data-id="element-2862">
              {[{
              label: 'Search businesses',
              done: true
            }, {
              label: 'Enrich contacts',
              done: true
            }, {
              label: 'Score leads',
              done: true
            }, {
              label: 'Draft outreach',
              active: true
            }, {
              label: 'Export results',
              done: false
            }].map((s, i) => <div key={i} className="flex items-center gap-2 relative z-10" data-id="element-2863">
                  <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${s.done ? 'bg-emerald-500 text-white' : s.active ? 'bg-blue-500 text-white ring-[3px] ring-blue-500/20' : 'bg-white border border-slate-200'}`} data-id="element-2864">
                    {s.done ? <CheckCircle2Icon className="w-2.5 h-2.5" data-id="element-2865" /> : s.active ? <Loader2Icon className="w-2 h-2 animate-spin" data-id="element-2866" /> : <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-2867" />}
                  </div>
                  <span className={`text-[10px] leading-tight ${s.done ? 'text-slate-400 line-through' : s.active ? 'text-slate-800 font-semibold' : 'text-slate-400'}`} data-id="element-2868">
                    {s.label}
                  </span>
                </div>)}
            </div>

            {/* Working folder */}
            <div className="mt-auto pt-3 border-t border-slate-200" data-id="element-2869">
              <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1" data-id="element-2870">
                Output files
              </div>
              <div className="space-y-1.5" data-id="element-2871">
                <div className="bg-white border border-slate-200 rounded-lg p-1.5 flex items-center gap-1.5" data-id="element-2872">
                  <FileSpreadsheetIcon className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-2873" />
                  <span className="text-[9px] text-slate-600 truncate flex-1" data-id="element-2874">
                    Leads.xlsx
                  </span>
                  <span className="text-[8px] text-emerald-500 font-medium shrink-0" data-id="element-2875">
                    312 rows
                  </span>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-1.5 flex items-center gap-1.5" data-id="element-2876">
                  <FileIcon className="w-3 h-3 text-blue-500 shrink-0" data-id="element-2877" />
                  <span className="text-[9px] text-slate-600 truncate flex-1" data-id="element-2878">
                    Scores.csv
                  </span>
                  <Loader2Icon className="w-2.5 h-2.5 text-blue-500 animate-spin shrink-0" data-id="element-2879" />
                </div>
                <div className="bg-white border border-slate-100 rounded-lg p-1.5 flex items-center gap-1.5 opacity-50" data-id="element-2880">
                  <SendIcon className="w-3 h-3 text-slate-400 shrink-0" data-id="element-2881" />
                  <span className="text-[9px] text-slate-400 truncate flex-1" data-id="element-2882">
                    Emails.md
                  </span>
                  <span className="text-[8px] text-slate-400 shrink-0" data-id="element-2883">
                    pending
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main execution area */}
          <div className="flex-1 flex flex-col bg-white overflow-hidden" data-id="element-2884">
            {/* Top bar */}
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-white" data-id="element-2885">
              <div className="flex items-center gap-2" data-id="element-2886">
                <div className="w-5 h-5 rounded-md bg-blue-600 flex items-center justify-center" data-id="element-2887">
                  <SparklesIcon className="w-3 h-3 text-white" data-id="element-2888" />
                </div>
                <span className="text-[12px] font-semibold text-slate-800" data-id="element-2889">
                  Dental clinics in Columbus, OH
                </span>
              </div>
              <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100" data-id="element-2890">
                Step 4 of 5
              </span>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto p-3.5 space-y-2.5" data-id="element-2891">
              {/* Completed steps - collapsed */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/60 border border-blue-100 rounded-lg" data-id="element-2892">
                <CheckCircle2Icon className="w-3 h-3 text-blue-500 shrink-0" data-id="element-2893" />
                <span className="text-[10px] text-blue-700 font-medium" data-id="element-2894">
                  Found 312 clinics
                </span>
                <span className="text-[10px] text-blue-400 mx-1" data-id="element-2895">·</span>
                <span className="text-[10px] text-blue-700 font-medium" data-id="element-2896">
                  Enriched contacts
                </span>
                <ChevronDownIcon className="w-3 h-3 text-blue-400 ml-auto" data-id="element-2897" />
              </div>

              {/* Scoring breakdown */}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 space-y-2" data-id="element-2898">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-2899">
                  <CheckCircle2Icon className="w-3 h-3 text-blue-500" data-id="element-2900" />
                  <span className="text-[11px] font-semibold text-slate-700" data-id="element-2901">
                    Lead scoring complete
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-2902">
                  <span className="text-slate-500" data-id="element-2903">Tier A (high fit)</span>
                  <span className="font-semibold text-slate-700" data-id="element-2904">48 leads</span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-2905">
                  <span className="text-slate-500" data-id="element-2906">Tier B (medium fit)</span>
                  <span className="font-semibold text-slate-700" data-id="element-2907">
                    127 leads
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-2908">
                  <span className="text-slate-500" data-id="element-2909">Tier C (low fit)</span>
                  <span className="font-medium text-slate-400" data-id="element-2910">137 leads</span>
                </div>
              </div>

              {/* Credit approval - compact, blue palette */}
              <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-3" data-id="element-2911">
                <div className="flex items-center gap-2 mb-2" data-id="element-2912">
                  <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-2913">
                    <DatabaseIcon className="w-2.5 h-2.5 text-blue-600" data-id="element-2914" />
                  </div>
                  <span className="text-[11px] font-semibold text-blue-900" data-id="element-2915">
                    Draft outreach for 48 Tier A leads
                  </span>
                  <span className="ml-auto text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-2916">
                    96 credits
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2917">
                  <button className="px-3.5 py-1.5 rounded-lg text-[10px] font-semibold text-white bg-blue-600 shadow-sm" data-id="element-2918">
                    Approve
                  </button>
                  <button className="px-3.5 py-1.5 rounded-lg text-[10px] font-medium text-blue-700 border border-blue-200 bg-white" data-id="element-2919">
                    Skip
                  </button>
                  <button className="ml-auto text-[10px] text-blue-500 flex items-center gap-1" data-id="element-2920">
                    <SettingsIcon className="w-3 h-3" data-id="element-2921" />
                    Adjust
                  </button>
                </div>
              </div>

              {/* Active status */}
              <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1" data-id="element-2922">
                <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-2923">
                  <SparklesIcon className="w-3.5 h-3.5 text-blue-500" data-id="element-2924" />
                </motion.div>
                Waiting for approval to draft emails...
              </div>
            </div>

            {/* Bottom status bar */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-2925">
              <div className="flex items-center gap-2 text-[10px] text-slate-400" data-id="element-2926">
                <ClockIcon className="w-3 h-3" data-id="element-2927" />
                Awaiting approval
              </div>
              <div className="text-[10px] text-slate-400" data-id="element-2928">
                Balance:{' '}
                <span className="font-semibold text-slate-600" data-id="element-2929">
                  4,200 credits
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
const illustrations = [Step1Illustration, Step2Illustration, Step3Illustration];
export function OpenclawCowork() {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ActiveIllustration = illustrations[activeStep];
  const startTimer = useCallback(() => {
    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    // Progress bar updates every 50ms
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(elapsed / STEP_DURATION * 100, 100);
      setProgress(pct);
    }, 50);
    // Auto-advance
    timerRef.current = setTimeout(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, STEP_DURATION);
  }, []);
  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [activeStep, startTimer]);
  const handleStepClick = (index: number) => {
    setActiveStep(index);
  };
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-2930">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-2931">
        {/* Header */}
        <div className="max-w-3xl mb-16" data-id="element-2932">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-bold mb-4 border border-blue-100" data-id="element-2933">
            <SparklesIcon className="w-4 h-4" data-id="element-2934" />
            Openclaw for sales
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-2935">
            One request. Structured workflow.
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed" data-id="element-2936">
            Describe your sales goal and Openclaw builds the full execution plan
            automatically.
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-2937">
          {/* Left: Vertical Steps */}
          <div className="lg:col-span-4 relative" data-id="element-2938">
            {/* Connecting Line */}
            <div className="absolute left-[23px] top-8 bottom-8 w-px bg-slate-100" data-id="element-2939" />

            <div className="space-y-2 relative z-10" data-id="element-2940">
              {steps.map((step, index) => {
              const isActive = activeStep === index;
              return <div key={step.id} className="relative" data-id="element-2941">
                    <button onClick={() => handleStepClick(index)} className="w-full text-left flex items-start gap-5 p-2 group cursor-pointer" data-id="element-2942">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-sm font-bold transition-all duration-300 ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20 ring-4 ring-white' : 'bg-slate-50 text-slate-400 border border-slate-200 group-hover:border-blue-300 group-hover:text-blue-500 ring-4 ring-white'}`} data-id="element-2943">
                        {step.id}
                      </div>

                      <div className="pt-3 pb-6 flex-1 min-w-0" data-id="element-2944">
                        <h4 className={`text-lg font-bold transition-colors duration-300 ${isActive ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-700'}`} data-id="element-2945">
                          {step.title}
                        </h4>

                        <AnimatePresence initial={false} data-id="element-2946">
                          {isActive && <motion.div initial={{
                        height: 0,
                        opacity: 0,
                        marginTop: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1,
                        marginTop: 8
                      }} exit={{
                        height: 0,
                        opacity: 0,
                        marginTop: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} className="overflow-hidden" data-id="element-2947">
                              {'description' in step && <p className="text-slate-500 leading-relaxed pr-4" data-id="element-2948">
                                  {step.description}
                                </p>}
                              {'highlights' in step && <ul className="space-y-1.5 pr-4" data-id="element-2949">
                                  {(step as any).highlights.map((h: string, hi: number) => <li key={hi} className="flex items-start gap-2 text-slate-500 text-sm leading-relaxed" data-id="element-2950">
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0 mt-1.5" data-id="element-2951" />
                                        {h}
                                      </li>)}
                                </ul>}
                              {/* Progress bar */}
                              <div className="mt-4 h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2952">
                                <div className="h-full bg-blue-500 rounded-full transition-none" style={{
                            width: `${progress}%`
                          }} data-id="element-2953" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </button>
                  </div>;
            })}
            </div>
          </div>

          {/* Right: Illustration Area */}
          <div className="lg:col-span-8" data-id="element-2954">
            <div className="relative rounded-3xl bg-slate-50 border border-slate-100 p-6 md:p-10 lg:p-12 overflow-hidden min-h-[500px] flex items-center justify-center" data-id="element-2955">
              {/* Subtle blue glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-400/[0.06] blur-[100px] rounded-full pointer-events-none" data-id="element-2956" />

              <div className="relative z-10 w-full" data-id="element-2957">
                <AnimatePresence mode="wait" data-id="element-2958">
                  <motion.div key={activeStep} initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.97
                }} transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} data-id="element-2959">
                    <ActiveIllustration data-id="element-2960" />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}